import { CommonModule } from "@angular/common";
import { Component, Input, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { IonicModule, ModalController } from "@ionic/angular";
import {
	AskFlaggerAnswer,
	AskFlaggerContextOptions,
	AskFlaggerDocChunk,
	AskFlaggerService,
	AskFlaggerSuggestedQuestion,
} from "src/app/core/services/ask-flagger.service";

interface AskFlaggerMessage {
	role: "user" | "assistant";
	content: string;
	sources?: AskFlaggerDocChunk[];
	showSources?: boolean;
}

@Component({
	selector: "ask-flagger-modal",
	templateUrl: "./ask-flagger-modal.html",
	styleUrls: ["./ask-flagger-modal.scss"],
	standalone: true,
	imports: [CommonModule, FormsModule, IonicModule],
})
export class AskFlaggerModalPage implements OnInit, AskFlaggerContextOptions {
	@Input() contextTitle = "FlaggerAI help";
	@Input() intro =
		"Ask about rider profiles, race entries, subscriptions, results, lap times, livestreams, clips, and track-owner features.";
	@Input() placeholder =
		"Ask about entries, subscriptions, results, lap times, livestreams...";
	@Input() suggestedQuestions: AskFlaggerSuggestedQuestion[] = [];
	@Input() initialQuestion = "";
	@Input() autoAskInitialQuestion = false;

	public question = "";
	public loading = false;
	public showAllSuggestions = false;
	public visibleQuestions: AskFlaggerSuggestedQuestion[] = [];
	private readonly defaultSuggestedQuestions: AskFlaggerSuggestedQuestion[] = [
		{
			label: "Create rider profile",
			question: "How do I create and manage rider profiles?",
		},
		{
			label: "Sign up for races",
			question: "How do I sign up for a race event?",
		},
		{
			label: "Find my entries",
			question: "Where can I see my race entries?",
		},
		{
			label: "View lap times",
			question: "How do I view lap times in FlaggerAI Central?",
		},
		{
			label: "Watch livestream",
			question: "How do I watch a live stream for an event?",
		},
		{
			label: "Find video clips",
			question: "How do race video clips work?",
		},
		{
			label: "Subscription access",
			question: "What does a rider subscription unlock?",
		},
		{
			label: "Track owner tools",
			question: "What can track owners do with FlaggerAI?",
		},
	];
	public messages: AskFlaggerMessage[] = [];

	constructor(
		private modalCtrl: ModalController,
		private askFlaggerService: AskFlaggerService
	) {}

	ngOnInit() {
		this.visibleQuestions = this.mergeSuggestedQuestions(
			this.suggestedQuestions,
			this.defaultSuggestedQuestions
		);
		this.question = this.initialQuestion || "";

		if (this.autoAskInitialQuestion && this.question.trim()) {
			setTimeout(() => void this.ask());
		}
	}

	public get hasAskedQuestion() {
		return this.messages.some((message) => message.role === "user");
	}

	public get visibleSuggestedQuestions() {
		return this.showAllSuggestions
			? this.visibleQuestions
			: this.visibleQuestions.slice(0, 4);
	}

	async dismiss() {
		await this.modalCtrl.dismiss();
	}

	async ask() {
		const question = this.question.trim();
		if (!question || this.loading) return;

		this.messages.push({ role: "user", content: question });
		this.question = "";
		this.loading = true;

		try {
			const response: AskFlaggerAnswer =
				await this.askFlaggerService.answerQuestionWithFlaggerDocs(question);
			this.messages.push({
				role: "assistant",
				content:
					response.answer ||
					"I do not have enough FlaggerAI documentation to answer that.",
				sources: response.chunks || [],
			});
		} catch (error: any) {
			this.messages.push({
				role: "assistant",
				content: this.getFriendlyErrorMessage(error),
			});
		} finally {
			this.loading = false;
		}
	}

	async askSuggestedQuestion(question: string) {
		if (this.loading) return;

		this.question = question;
		await this.ask();
	}

	toggleSources(message: AskFlaggerMessage) {
		message.showSources = !message.showSources;
	}

	onQuestionKeydown(event: KeyboardEvent) {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			this.ask();
		}
	}

	trackByMessage(index: number) {
		return index;
	}

	trackBySource(index: number, source: AskFlaggerDocChunk) {
		return source.uid || `${source.header || "source"}-${index}`;
	}

	trackBySuggestion(index: number, suggestion: AskFlaggerSuggestedQuestion) {
		return `${suggestion.label}-${index}`;
	}

	sourceTitle(source: AskFlaggerDocChunk) {
		return (
			(source.header || source.category || "FlaggerAI Documentation").replace(
				/^#+\s*/,
				""
			) || "FlaggerAI Documentation"
		);
	}

	private getFriendlyErrorMessage(error: any) {
		const rawMessage = `${error?.message || error?.details || ""}`;
		const lowerMessage = rawMessage.toLowerCase();

		if (
			lowerMessage.includes("no enough flaggerai documentation") ||
			lowerMessage.includes("documentation indexed") ||
			lowerMessage.includes("search index")
		) {
			return "Ask Flagger does not have Central documentation indexed yet. An admin needs to open Admin -> Rebuild Ask Flagger Search Index.";
		}

		if (
			lowerMessage.includes("openai") ||
			lowerMessage.includes("api key") ||
			lowerMessage.includes("chatgpt") ||
			lowerMessage.includes("secret")
		) {
			return "Ask Flagger is not configured yet. Check the OpenAI credential used by this Firebase project.";
		}

		if (
			lowerMessage.includes("functions") ||
			lowerMessage.includes("callable") ||
			lowerMessage.includes("network") ||
			lowerMessage.includes("unavailable") ||
			lowerMessage.includes("not found")
		) {
			return "Ask Flagger could not reach the documentation service. Check that functions are deployed and try again.";
		}

		return "Ask Flagger is not available right now. Try again after the documentation search index has been rebuilt.";
	}

	private mergeSuggestedQuestions(
		contextQuestions: AskFlaggerSuggestedQuestion[],
		defaultQuestions: AskFlaggerSuggestedQuestion[]
	): AskFlaggerSuggestedQuestion[] {
		const seen = new Set<string>();
		return [...(contextQuestions || []), ...defaultQuestions].filter((item) => {
			const key = `${item.label}::${item.question}`.toLowerCase();
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	}
}
